import { useState, useMemo, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../Icons';
import { fmt } from '../../utils/formatters';
// ✨ Cost Basis Chain Tracking - Track performance through swap chains
import { calculateDualPerformance } from '../../utils/costBasisTracking';
import { SwapChainVisualization } from './SwapChainVisualization';
import { SwapNavigator } from './SwapNavigator';
import { FeatureTooltip } from '../onboarding/FeatureTooltip';
import { DataStateCard } from '../ui/DataStateCard';
import type { PortfolioTableProps, Token, Position } from '../../types';

// Lazy load heavy chart component
const OriginPerformanceChart = lazy(() => import('./OriginPerformanceChart').then(m => ({ default: m.OriginPerformanceChart })));

interface TokenMetrics {
  qty: number;
  avgCost: number;
  currentPrice: number;
  change24h: number;
  currentValue: number;
  totalCost: number;
  unrealizedPL: number;
  unrealizedPct: number;
  realizedPL: number;
  // ✨ Cost Basis Chain Tracking
  originPerformance: number | null;  // Performance depuis investissement initial (via swaps)
  hasSwapHistory: boolean;           // True si token issu de swaps
}

/**
 * PortfolioTable Component
 * Affiche le portfolio avec métriques FIFO/LIFO et historique des positions
 */
export function PortfolioTable({
  tokens,
  prices,
  fifoResults,
  onAddPosition,
  onAddFirstToken,
  onDeleteToken,
  onEditPosition,
  onDeletePosition,
  onSwapToken,
  onUpdateTarget,
  showBTCComparison = false,
  btcHistoricalPrices = {},
  btcCurrentPrice = 0,
  isNewToken,
  showTargetColumn = true,
  isExpertMode = false
}: PortfolioTableProps) {
  const { t } = useTranslation();
  const [expandedTokens, setExpandedTokens] = useState<Set<string>>(new Set());
  const [editingTarget, setEditingTarget] = useState<string | null>(null);
  const [targetInput, setTargetInput] = useState<string>('');

  const toggleExpand = (tokenId: string) => {
    const newExpanded = new Set(expandedTokens);
    if (newExpanded.has(tokenId)) {
      newExpanded.delete(tokenId);
    } else {
      newExpanded.add(tokenId);
    }
    setExpandedTokens(newExpanded);
  };

  const handleEditTarget = (token: Token) => {
    setEditingTarget(token.id);
    setTargetInput(token.targetPrice?.toString() || '');
  };

  const handleSaveTarget = (tokenId: string) => {
    const target = parseFloat(targetInput);
    if (!isNaN(target) && target > 0 && onUpdateTarget) {
      onUpdateTarget(tokenId, target);
    } else if (targetInput === '' && onUpdateTarget) {
      onUpdateTarget(tokenId, null);
    }
    setEditingTarget(null);
    setTargetInput('');
  };

  const handleCancelEdit = () => {
    setEditingTarget(null);
    setTargetInput('');
  };

  // Memoize metrics calculation for all tokens to avoid recalculation
  const tokenMetricsMap = useMemo(() => {
    const metricsMap = new Map<string, TokenMetrics>();

    tokens.forEach(token => {
      const fifo = fifoResults[token.id] || {};
      const currentPrice = prices[token.id]?.usd || 0;
      const change24h = prices[token.id]?.usd_24h_change || 0;
      const qty = fifo.remainingQty || 0;
      const avgCost = fifo.avgCost || 0;
      const currentValue = qty * currentPrice;
      const totalCost = qty * avgCost;
      const unrealizedPL = currentValue - totalCost;
      const unrealizedPct = totalCost > 0 ? (unrealizedPL / totalCost) * 100 : 0;

      // Calculate dual performance (current vs origin)
      const dualPerf = calculateDualPerformance(token, currentPrice);

      metricsMap.set(token.id, {
        qty,
        avgCost,
        currentPrice,
        change24h,
        currentValue,
        totalCost,
        unrealizedPL,
        unrealizedPct,
        realizedPL: fifo.realizedPL || 0,
        originPerformance: dualPerf.originPerformance,
        hasSwapHistory: dualPerf.hasSwapHistory
      });
    });

    return metricsMap;
  }, [tokens, prices, fifoResults]);

  // Helper to get metrics for a token
  const getMetrics = (tokenId: string): TokenMetrics => {
    return tokenMetricsMap.get(tokenId) || {
      qty: 0, avgCost: 0, currentPrice: 0, change24h: 0,
      currentValue: 0, totalCost: 0, unrealizedPL: 0, unrealizedPct: 0,
      realizedPL: 0, originPerformance: null, hasSwapHistory: false
    };
  };

  // Calculate portfolio totals from memoized metrics
  const { totalPortfolio, totalUnrealizedPL } = useMemo(() => {
    let portfolio = 0;
    let unrealized = 0;

    tokenMetricsMap.forEach(metrics => {
      portfolio += metrics.currentValue;
      unrealized += metrics.unrealizedPL;
    });

    return { totalPortfolio: portfolio, totalUnrealizedPL: unrealized };
  }, [tokenMetricsMap]);

  if (tokens.length === 0) {
    return (
      <DataStateCard
        variant="empty"
        icon={<Icons.Package size={48} />}
        title={t('portfolioTable.noTokens')}
        message={t('portfolioDashboard.tipStartWith', 'Astuce: Commencez avec les cryptos que vous possédez réellement (BTC, ETH, SOL...)')}
        action={
          <button onClick={() => onAddFirstToken && onAddFirstToken()}>
            <Icons.Plus size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
            {t('portfolioTable.addFirstToken')}
          </button>
        }
      />
    );
  }

  return (
    <div className="card">
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ fontSize: '16px', margin: 0 }}>
          Portfolio ({tokens.length} tokens)
        </h2>
        <div style={{ fontSize: '14px' }}>
          <strong>Total:</strong> {fmt(totalPortfolio, 0)} $
          <span className={totalUnrealizedPL >= 0 ? 'positive' : 'negative'} style={{ marginLeft: '12px' }}>
            {totalUnrealizedPL >= 0 ? '+' : ''}{fmt(totalUnrealizedPL, 0)} $
          </span>
        </div>
      </div>

      <div className="table-wrapper" style={{ overflowX: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th style={{ width: '30px' }}></th>
              <th>{t('portfolioTable.tokenTransactions')}</th>
              <th>24h</th>
              <th>{t('portfolioTable.quantity')}</th>
              <th>{t('portfolioTable.avgPrice')}</th>
              <th>{t('portfolioTable.currentPrice')}</th>
              {showTargetColumn && <th title={t('portfolioTable.targetTooltip')}>🎯 Target</th>}
              <th>{t('portfolioTable.value')}</th>
              <th>P/L $</th>
              <th>P/L %</th>
              {showBTCComparison && <th>% vs BTC</th>}
              <th>{t('portfolioTable.realized')}</th>
              <th>{t('portfolioTable.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {tokens.map(token => {
              const metrics = getMetrics(token.id);
              const isExpanded = expandedTokens.has(token.id);
              const sortedPositions = [...token.positions].sort((a, b) =>
                new Date(b.date).getTime() - new Date(a.date).getTime()
              );

              // Calculate % vs BTC for this token (weighted average of buy positions)
              // V2.H.7 : returns null when btcHistoricalPrices doesn't have the
              // data we need — surfaces as "—" in the UI instead of a misleading
              // "+0.00%" that looks like a real metric.
              const tokenVsBTC: number | null = (() => {
                if (!showBTCComparison || btcCurrentPrice === 0) return null;

                let totalBuyValue = 0;
                let weightedSum = 0;
                let matchedPositions = 0;

                token.positions.forEach(position => {
                  if (position.type === 'buy') {
                    const buyValue = position.quantity * position.price;
                    const btcPriceAtBuy = btcHistoricalPrices[position.date] || 0;

                    if (btcPriceAtBuy > 0) {
                      const currentPrice = prices[token.id]?.usd || 0;

                      // Token performance
                      const tokenPerf = ((currentPrice - position.price) / position.price) * 100;

                      // BTC performance
                      const btcPerf = ((btcCurrentPrice - btcPriceAtBuy) / btcPriceAtBuy) * 100;

                      // Difference
                      const vsBTC = tokenPerf - btcPerf;

                      totalBuyValue += buyValue;
                      weightedSum += buyValue * vsBTC;
                      matchedPositions++;
                    }
                  }
                });

                // No BTC historical price for any of our positions → unknown,
                // not zero. Show "—" rather than "+0.00%".
                if (matchedPositions === 0 || totalBuyValue === 0) return null;
                return weightedSum / totalBuyValue;
              })();

              return (
                <>
                  {/* Main token row */}
                  <tr
                    key={token.id}
                    className={isNewToken?.(token.id) ? 'shimmer-highlight' : ''}
                    style={{ cursor: 'pointer', backgroundColor: isExpanded ? 'var(--bg-secondary)' : undefined }}
                  >
                    <td onClick={() => toggleExpand(token.id)} style={{ textAlign: 'center' }}>
                      {isExpanded ? <Icons.ChevronDown size={16} /> : <Icons.ChevronRight size={16} />}
                    </td>
                    <td onClick={() => toggleExpand(token.id)} style={{ textAlign: 'left', fontWeight: 600 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span>{token.displayName || token.id}</span>
                        {token.positions.length > 1 && (
                          <span className="badge-info" style={{ fontSize: '10px', padding: '2px 6px' }}>
                            {token.positions.length} tx
                          </span>
                        )}
                      </div>
                      <span style={{ fontSize: '11px', color: 'var(--muted)' }}>
                        {token.id}
                      </span>
                    </td>
                    <td onClick={() => toggleExpand(token.id)} style={{ fontSize: '11px', color: 'var(--muted)' }}>
                      <span className={metrics.change24h >= 0 ? 'positive' : 'negative'}>
                        {metrics.change24h >= 0 ? '+' : ''}{fmt(metrics.change24h, 2)}%
                      </span>
                    </td>
                    <td onClick={() => toggleExpand(token.id)}>{fmt(metrics.qty, 8)}</td>
                    <td onClick={() => toggleExpand(token.id)}>{fmt(metrics.avgCost, 2)} $</td>
                    <td onClick={() => toggleExpand(token.id)}>{fmt(metrics.currentPrice, 2)} $</td>
                    {showTargetColumn && (
                      <td onClick={(e) => e.stopPropagation()}>
                        {editingTarget === token.id ? (
                          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                            <input
                              type="number"
                              step="0.01"
                              value={targetInput}
                              onChange={(e) => setTargetInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSaveTarget(token.id);
                                if (e.key === 'Escape') handleCancelEdit();
                              }}
                              autoFocus
                              placeholder="Target $"
                              style={{
                                width: '80px',
                                padding: '4px 6px',
                                border: '1px solid var(--accent)',
                                borderRadius: '4px',
                                background: 'var(--bg)',
                                color: 'var(--text)',
                                fontSize: '12px'
                              }}
                            />
                            <button
                              onClick={() => handleSaveTarget(token.id)}
                              style={{
                                padding: '2px 6px',
                                background: 'var(--accent)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '11px'
                              }}
                              title={t('portfolioTable.saveEnter')}
                            >
                              ✓
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              style={{
                                padding: '2px 6px',
                                background: 'var(--bg-secondary)',
                                color: 'var(--text)',
                                border: '1px solid var(--border)',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '11px'
                              }}
                              title={t('portfolioTable.cancelEscape')}
                            >
                              ✕
                            </button>
                          </div>
                        ) : (
                          <div
                            onClick={() => onUpdateTarget && handleEditTarget(token)}
                            style={{
                              cursor: onUpdateTarget ? 'pointer' : 'default',
                              padding: '4px',
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              transition: 'background 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              if (onUpdateTarget) e.currentTarget.style.background = 'var(--bg-secondary)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'transparent';
                            }}
                            title={onUpdateTarget ? t('portfolioTable.clickToSetTarget') : undefined}
                          >
                            {token.targetPrice ? (
                              <>
                                <span>{fmt(token.targetPrice, 2)} $</span>
                                {onUpdateTarget && (
                                  <Icons.Edit size={12} style={{ opacity: 0.5 }} />
                                )}
                              </>
                            ) : (
                              <span style={{ color: 'var(--muted)', fontSize: '12px' }}>
                                {onUpdateTarget ? t('portfolioTable.setTarget') : '-'}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                    )}
                    <td onClick={() => toggleExpand(token.id)}>{fmt(metrics.currentValue, 0)} $</td>
                    <td onClick={() => toggleExpand(token.id)} className={metrics.unrealizedPL >= 0 ? 'positive' : 'negative'}>
                      {metrics.unrealizedPL >= 0 ? '+' : ''}{fmt(metrics.unrealizedPL, 0)} $
                    </td>
                    <td onClick={() => toggleExpand(token.id)}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'flex-end' }}>
                        {/* Performance token actuel */}
                        <span className={metrics.unrealizedPct >= 0 ? 'positive' : 'negative'}>
                          {metrics.unrealizedPct >= 0 ? '+' : ''}{fmt(metrics.unrealizedPct, 2)}%
                        </span>

                        {/* ✨ Performance origine (si swap) */}
                        {metrics.hasSwapHistory && metrics.originPerformance !== null && (
                          <span
                            className={metrics.originPerformance >= 0 ? 'positive' : 'negative'}
                            style={{ fontSize: '10px', opacity: 0.8 }}
                            title={t('portfolioTable.originPerformanceTooltip')}
                          >
                            📊 {metrics.originPerformance >= 0 ? '+' : ''}{fmt(metrics.originPerformance, 2)}%
                          </span>
                        )}
                      </div>
                    </td>
                    {showBTCComparison && (
                      <td
                        onClick={() => toggleExpand(token.id)}
                        className={
                          tokenVsBTC == null ? '' : tokenVsBTC >= 0 ? 'positive' : 'negative'
                        }
                      >
                        {tokenVsBTC == null
                          ? '—'
                          : `${tokenVsBTC >= 0 ? '+' : ''}${fmt(tokenVsBTC, 2)}%`}
                      </td>
                    )}
                    <td onClick={() => toggleExpand(token.id)} className={metrics.realizedPL >= 0 ? 'positive' : 'negative'}>
                      {metrics.realizedPL >= 0 ? '+' : ''}{fmt(metrics.realizedPL, 0)} $
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                        <FeatureTooltip featureId="add_position" position="left">
                          <button
                            className="btn-ghost"
                            onClick={() => onAddPosition && onAddPosition(token)}
                            title={t('portfolioTable.addPosition')}
                            style={{ padding: '4px 8px', fontSize: '12px' }}
                          >
                            <Icons.Plus size={14} />
                          </button>
                        </FeatureTooltip>
                        {onSwapToken && (
                          <FeatureTooltip featureId="swap_tokens" position="left">
                            <button
                              className="btn-ghost"
                              onClick={() => onSwapToken(token)}
                              title={t('portfolioTable.swapToken')}
                              style={{ padding: '4px 8px', fontSize: '12px' }}
                            >
                              <Icons.Repeat size={14} />
                            </button>
                          </FeatureTooltip>
                        )}
                        <button
                          className="btn-ghost"
                          onClick={() => onDeleteToken && onDeleteToken(token.id)}
                          title={t('portfolioTable.deleteToken')}
                          style={{ padding: '4px 8px', fontSize: '12px' }}
                        >
                          <Icons.Trash size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>

                  {/* Expanded positions rows */}
                  {isExpanded && sortedPositions.map((position, idx) => {
                    const currentPrice = prices[token.id]?.usd || 0;
                    const positionCost = position.quantity * position.price;
                    const positionValue = position.quantity * currentPrice;
                    const unrealizedPL = position.type === 'buy' ? positionValue - positionCost : 0;
                    const unrealizedPct = position.type === 'buy' && positionCost > 0
                      ? (unrealizedPL / positionCost) * 100
                      : 0;

                    // Detect if this is a swap transaction
                    const isSwap = position.note?.toLowerCase().includes('swap vers') ||
                                   position.note?.toLowerCase().includes('swap depuis');

                    // Calculate % vs BTC for this position. Returns null when no
                    // BTC historical price for the buy date (avoid showing fake 0 %).
                    const positionVsBTC: number | null = (() => {
                      if (!showBTCComparison || btcCurrentPrice === 0 || position.type !== 'buy') return null;

                      const btcPriceAtBuy = btcHistoricalPrices[position.date] || 0;
                      if (btcPriceAtBuy === 0) return null;

                      const tokenPerf = ((currentPrice - position.price) / position.price) * 100;
                      const btcPerf = ((btcCurrentPrice - btcPriceAtBuy) / btcPriceAtBuy) * 100;

                      return tokenPerf - btcPerf;
                    })();

                    return (
                      <tr key={`${token.id}-${position.id}`} style={{ backgroundColor: 'var(--bg-secondary)' }}>
                        <td></td>
                        <td style={{ paddingLeft: '30px', fontSize: '12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                            <span className={position.type === 'buy' ? 'badge-success' : 'badge-danger'}>
                              {position.type === 'buy' ? t('portfolioTable.buy') : t('portfolioTable.sell')}
                            </span>
                            {isSwap && (
                              <span className="badge-swap">
                                🔄 SWAP
                              </span>
                            )}
                          </div>
                        </td>
                        <td style={{ fontSize: '12px', color: 'var(--muted)' }}>
                          {position.date}
                        </td>
                        <td style={{ fontSize: '12px' }}>{fmt(position.quantity, 8)}</td>
                        <td style={{ fontSize: '12px' }}>{fmt(position.price, 2)} $</td>
                        <td style={{ fontSize: '12px', color: 'var(--muted)' }}>{fmt(currentPrice, 2)} $</td>
                        <td style={{ fontSize: '12px' }}>{fmt(positionCost, 2)} $</td>
                        <td style={{ fontSize: '12px' }} className={unrealizedPL >= 0 ? 'positive' : 'negative'}>
                          {position.type === 'buy'
                            ? `${unrealizedPL >= 0 ? '+' : ''}${fmt(unrealizedPL, 0)} $`
                            : <span style={{ color: 'var(--muted)', fontStyle: 'italic' }}>{t('portfolioTable.realized')}</span>
                          }
                        </td>
                        <td style={{ fontSize: '12px' }} className={unrealizedPct >= 0 ? 'positive' : 'negative'}>
                          {position.type === 'buy'
                            ? `${unrealizedPct >= 0 ? '+' : ''}${fmt(unrealizedPct, 2)}%`
                            : <span style={{ color: 'var(--muted)', fontStyle: 'italic' }}>-</span>
                          }
                        </td>
                        {showBTCComparison && (
                          <td style={{ fontSize: '12px' }} className={positionVsBTC == null ? '' : positionVsBTC >= 0 ? 'positive' : 'negative'}>
                            {position.type === 'buy'
                              ? (positionVsBTC == null
                                  ? <span style={{ color: 'var(--muted)' }} title="Pas de prix BTC historique pour cette date">—</span>
                                  : `${positionVsBTC >= 0 ? '+' : ''}${fmt(positionVsBTC, 2)}%`)
                              : <span style={{ color: 'var(--muted)', fontStyle: 'italic' }}>-</span>
                            }
                          </td>
                        )}
                        <td style={{ fontSize: '12px', fontStyle: 'italic', color: 'var(--muted)' }}>
                          {position.note || '-'}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                            <button
                              className="btn-ghost"
                              onClick={() => onEditPosition && onEditPosition(token, position)}
                              title={t('common.edit')}
                              style={{ padding: '4px 8px', fontSize: '11px' }}
                            >
                              <Icons.Edit size={12} />
                            </button>
                            <button
                              className="btn-ghost"
                              onClick={() => onDeletePosition && onDeletePosition(token.id, position.id)}
                              title={t('common.delete')}
                              style={{ padding: '4px 8px', fontSize: '11px' }}
                            >
                              <Icons.Trash size={12} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}

                  {/* ✨ Swap Chain Features - Toutes les fonctionnalités de tracking des swaps */}
                  {isExpanded && (
                    <>
                      {/* ✨ Swap Chain Visualization - Timeline complète (Expert Mode Only) */}
                      {isExpertMode && (
                        <tr className="no-hover">
                          <td colSpan={showBTCComparison ? (showTargetColumn ? 11 : 10) : (showTargetColumn ? 10 : 9)} style={{ padding: 0 }}>
                            <SwapChainVisualization
                              currentToken={token}
                              allTokens={tokens}
                              prices={prices}
                            />
                          </td>
                        </tr>
                      )}

                      {/* ✨ Swap Navigator - Navigation interactive (Expert Mode Only) */}
                      {isExpertMode && metrics.hasSwapHistory && (
                        <tr className="no-hover">
                          <td colSpan={showBTCComparison ? (showTargetColumn ? 11 : 10) : (showTargetColumn ? 10 : 9)} style={{ padding: 0 }}>
                            <SwapNavigator
                              currentToken={token}
                              allTokens={tokens}
                              prices={prices}
                            />
                          </td>
                        </tr>
                      )}

                      {/* ✨ Origin Performance Chart - Graphique historique (All Modes) */}
                      {metrics.hasSwapHistory && (
                        <tr className="no-hover">
                          <td colSpan={showBTCComparison ? (showTargetColumn ? 11 : 10) : (showTargetColumn ? 10 : 9)} style={{ padding: 0 }}>
                            <Suspense fallback={
                              <div style={{ padding: '24px', textAlign: 'center', color: 'var(--muted)' }}>
                                <Icons.Loader size={20} style={{ animation: 'spin 1s linear infinite' }} />
                                <div style={{ marginTop: '8px', fontSize: '12px' }}>{t('charts.loading')}</div>
                              </div>
                            }>
                              <OriginPerformanceChart
                                currentToken={token}
                                prices={prices}
                                days={90}
                              />
                            </Suspense>
                          </td>
                        </tr>
                      )}
                    </>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
